/**
 * Real Data Policy — link integrity helpers.
 * Shared by the legacy itinerary-style cards (still used in the multi-city
 * storytelling render) and the marketplace inventory row.
 */

// ─── Provider Deep Link Integrity V1 — strict Viator product-URL check ─────
// A generic http(s) + non-mock-pattern check accepts Viator's own generic
// landing/search/category pages (e.g. "https://www.viator.com/things-to-do/
// Madrid"), which is exactly what let a "Ver experiencia" CTA point at a
// page with many activities instead of the one product the user selected.
// This mirrors the backend's engine.providers.viator.viator_url_validator
// exactly: host contains "viator.com", path contains "/tours/", and path
// has a destination-id + product-code segment like "/d566-39034P15".

const VIATOR_HOST_MARKER = "viator.com";
const VIATOR_PRODUCT_SEGMENT = /\/d\d+-[a-z0-9]{2,}/i;

function isViatorHostname(hostname: string): boolean {
  const host = hostname.toLowerCase().replace(/\.+$/, "");
  return host === VIATOR_HOST_MARKER || host.endsWith(`.${VIATOR_HOST_MARKER}`);
}

function isViatorHost(url: string): boolean {
  try {
    return isViatorHostname(new URL(url).hostname);
  } catch {
    return false;
  }
}

/** Strict structural check: does this URL look like a specific Viator product page? */
export function isViatorProductUrl(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return false;
  if (!isViatorHostname(parsed.hostname)) return false;
  const path = parsed.pathname.toLowerCase();
  if (!path.includes("/tours/")) return false;
  return VIATOR_PRODUCT_SEGMENT.test(parsed.pathname);
}

/**
 * Return true when a URL is suitable for showing as an active user-facing link.
 * False for: empty, non-http(s), mock patterns, localhost, example.com, and
 * (Provider Deep Link Integrity V1) generic Viator landing/search/category
 * pages that aren't a specific product.
 *
 * Also respects the backend's link_available signal when present on the item.
 * This is the frontend mirror of backend._is_link_available() +
 * is_valid_provider_booking_url().
 */
export function isUserFacingValidUrl(url: string | null | undefined): boolean {
  if (!url || url.trim() === "") return false;
  const lower = url.trim().toLowerCase();
  if (!lower.startsWith("http://") && !lower.startsWith("https://")) return false;
  const mockPatterns = [
    "example.com",
    "localhost",
    "placeholder",
    "/mock",
    "mock.",
  ];
  if (mockPatterns.some(p => lower.includes(p))) return false;
  // Detect Viator mock product IDs: /d\d+-[A-Z]{2,4}\d+
  if (/\/d\d+-[a-z]{2,4}\d+/i.test(url)) return false;
  if (isViatorHost(url) && !isViatorProductUrl(url)) return false;
  return true;
}
